import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Eye, Send, Loader2, Download, CheckCircle2, Pencil, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Inline PDF preview dialog for secretariat: shows the generated rješenje
 * in an iframe and lets secretary embed an e-signature (prepared_by) before
 * sending. Same-origin cookie auth handles PDF fetching.
 *
 * Iter103: dodano "Uredi tekst rješenja" za vacation — otvori side textarea,
 * spremi kroz POST /generate-pdf (custom_body_text) i reloadaj iframe.
 */
export function PdfPreviewDialog({
  preview,              // { id, type, employee_name, pdfUrl } | null
  defaultSignature,     // string — pre-fills "Pripremio/la" input
  onClose,              // () => void
  onSend,               // (preparedBy: string) => Promise<void>
  onDownload,           // (id, type) => void
}) {
  const [signature, setSignature] = useState("");
  const [sending, setSending] = useState(false);
  // Iter103: text edit state
  const [editing, setEditing] = useState(false);
  const [bodyText, setBodyText] = useState("");
  const [defaultBody, setDefaultBody] = useState("");
  const [loadingBody, setLoadingBody] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (preview) {
      setSignature(defaultSignature || "");
      setEditing(false);
      setBodyText("");
      setDefaultBody("");
      setReloadTick(0);
    }
  }, [preview, defaultSignature]);

  const openEditor = async () => {
    if (!preview || preview.type !== "vacation") return;
    setLoadingBody(true);
    try {
      const r = await api.get(`/vacation/requests/${preview.id}/default-body`);
      setDefaultBody(r.data.default_body_text || "");
      setBodyText(r.data.current_custom_body_text || r.data.default_body_text || "");
      setEditing(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ne mogu dohvatiti predložak");
    } finally { setLoadingBody(false); }
  };

  const saveAndReload = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      // POST /generate-pdf s klasa/urbroj="" preserve postojeće, ali update custom_body_text
      await api.post(`/vacation/requests/${preview.id}/generate-pdf`, {
        klasa: "",
        urbroj: "",
        custom_body_text: bodyText,
      });
      toast.success("Tekst rješenja spremljen — osvježavam pregled");
      setEditing(false);
      setReloadTick(t => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri spremanju");
    } finally { setSaving(false); }
  };

  const restoreDefault = () => {
    setBodyText(defaultBody);
    toast.info("Vraćeno na default tekst — možete uređivati dalje");
  };

  const clearCustom = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await api.post(`/vacation/requests/${preview.id}/generate-pdf`, {
        klasa: "",
        urbroj: "",
        custom_body_text: "",  // empty → koristi default
      });
      toast.info("Custom tekst uklonjen — koristi se automatski generiran default");
      setEditing(false);
      setReloadTick(t => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setSaving(false); }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend(signature.trim());
    } finally { setSending(false); }
  };

  const handleClose = () => { setSignature(""); setEditing(false); onClose(); };

  const pdfUrlWithTick = preview?.pdfUrl
    ? `${preview.pdfUrl}${reloadTick ? `&r=${reloadTick}` : ""}#toolbar=1&navpanes=0&zoom=page-fit`
    : "";

  return (
    <Dialog open={!!preview} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0" data-testid="pdf-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="serif text-2xl flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary"/>Pregled rješenja prije slanja
          </DialogTitle>
          {preview && (
            <p className="text-xs text-muted-foreground">
              Zaposlenik: <span className="font-medium">{preview.employee_name}</span> · Tip: {preview.type === "wfh" ? "Rad na daljinu" : "Godišnji odmor"}
              <br/>Provjerite pečat, memorandum i sadržaj. Ako uočite grešku, kliknite <b>&quot;Uredi tekst&quot;</b>. Kad je sve uredu — <b>&quot;Pošalji odmah&quot;</b>.
            </p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-secondary/30 flex">
          {/* PDF iframe */}
          <div className={`${editing ? "w-1/2 border-r" : "w-full"} h-full transition-all`}>
            {!preview?.pdfUrl ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin"/>Učitavanje PDF pregleda…
              </div>
            ) : (
              <iframe
                src={pdfUrlWithTick}
                title="Pregled PDF rješenja"
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
                key={reloadTick}
              />
            )}
          </div>
          {/* Editor sidebar (Iter103) */}
          {editing && (
            <div className="w-1/2 h-full overflow-y-auto p-4 space-y-3 bg-amber-50/40" data-testid="preview-edit-panel">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Pencil className="w-4 h-4"/> Uredi tekst rješenja (točke I - IV)
                </Label>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={restoreDefault} data-testid="preview-edit-reset">
                    <RotateCcw className="w-3 h-3 mr-1"/> Default
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clearCustom} disabled={saving} data-testid="preview-edit-clear">
                    Ukloni custom
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="preview-edit-cancel">
                    Zatvori
                  </Button>
                </div>
              </div>
              <Textarea
                rows={22}
                className="font-mono text-xs leading-relaxed h-[calc(100%-140px)] min-h-[300px]"
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                data-testid="preview-edit-textarea"
              />
              <div className="text-[11px] text-muted-foreground space-y-0.5">
                <p>• Redovi <b>I.</b>, <b>II.</b>, <b>III.</b>, <b>IV.</b> renderiraju se kao bold naslovi.</p>
                <p>• Prazan red = razmak između paragrafa. Za bold u tekstu: <code className="bg-secondary px-1 rounded text-[10px]">&lt;b&gt;tekst&lt;/b&gt;</code></p>
              </div>
              <Button
                className="w-full"
                onClick={saveAndReload}
                disabled={saving || !bodyText.trim()}
                data-testid="preview-edit-save"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Save className="w-4 h-4 mr-1"/>}
                Spremi izmjene i osvježi pregled
              </Button>
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t bg-background space-y-3" data-testid="preview-footer">
          <div className="space-y-1.5">
            <Label htmlFor="prepared-by-input" className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary"/>E-potpis pripreme (vaše ime ili inicijali)
            </Label>
            <Input
              id="prepared-by-input"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="npr. Tomislav Pavković ili TP"
              className="max-w-md"
              data-testid="prepared-by-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Bit će uvršteno u footer PDF-a kao &quot;Pripremio/la: {signature || "…"}&quot; i zabilježeno u audit logu. Ostavite prazno za slanje bez potpisa pripreme.
            </p>
          </div>
          <div className="flex justify-end flex-wrap gap-2">
            <Button variant="outline" onClick={handleClose} data-testid="preview-close">
              Spremno za slanje (kasnije)
            </Button>
            {/* Iter103: Uredi tekst gumb (samo za vacation) */}
            {preview?.type === "vacation" && !editing && (
              <Button variant="outline" onClick={openEditor} disabled={loadingBody} data-testid="preview-edit-open">
                {loadingBody ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Pencil className="w-4 h-4 mr-1"/>}
                Uredi tekst
              </Button>
            )}
            {preview?.pdfUrl && (
              <Button variant="outline" onClick={() => onDownload(preview.id, preview.type)} data-testid="preview-download">
                <Download className="w-4 h-4 mr-1"/>Preuzmi PDF
              </Button>
            )}
            <Button onClick={handleSend} disabled={sending || !preview?.pdfUrl || editing} data-testid="preview-send">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Send className="w-4 h-4 mr-1"/>}
              Pošalji odmah
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
